'use client';

import { parseISO } from 'date-fns-modern';
import { useParams, useRouter } from 'next/navigation';
import React, { useEffect, useState } from 'react';

import { TDSScheduleCityToCityRequestBody } from '@/apis/tds';
import { doTranslation } from '@/app/i18n/client';
import { SearchType } from '@/app/types';
import FindBoardingPass from '@/components/global/boarding-pass/find-boarding-pass';
import Button from '@/components/global/buttons/button';
import ButtonTertiary from '@/components/global/buttons/button-tertiary';
import Modal from '@/components/global/modal/modal';
import Search from '@/components/global/search/search';
import ToastError from '@/components/global/toasts/toast-error';
import Loader from '@/components/loaders/loader';
import { useCustomerDetail } from '@/context/customer-detail-context';
import { useLogin } from '@/context/login-context';
import { useTripSearch } from '@/context/trip-search-context';
import { localeSpecificPath } from '@/utils/localeUtils';
import { default as Image } from '@/components/global/images/resized-cdn-image';

import styles from '@/components/home-page/home-page-hero.module.scss';

const HomePageHero = (): React.JSX.Element => {
  const { lng }: any = useParams();
  const { t } = doTranslation(lng);
  const router = useRouter();
  const { customer, loyalty } = useCustomerDetail();
  const [isTabletOrMobile, setIsTabletOrMobile] = useState(
    () => typeof window !== 'undefined' && window.innerWidth <= 1279
  );
  const [isOpen, setIsOpen] = useState(false);
  const [boardingPassError, setBoardingPassError] = useState(false);
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.innerWidth <= 600);
  const [passengerOptions, setPassengerOptions] = useState(false);
  const defaultText = t('Explore New Roads Together');
  const defaultSubtitle = t(
    'Redeem these points for exclusive discounts or even free bus tickets. Join our loyalty program and unlock a world of savings and benefits!'
  );
  const [subtitle, setSubtitle] = useState(defaultSubtitle);
  const [tripSearchError, setTripSearchError] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const {
    performTripSearch,
    redirectMessage,
    resetTrip,
    setRedirectMessage,
    setLoaderProps,
    loaderProps,
    tripDetailsForm,
    withLoader
  } = useTripSearch();
  const { userCheckedOut, setUserCheckedOut } = useLogin();

  useEffect(() => {
    const handleResize = () => {
      setIsTabletOrMobile(window.innerWidth <= 1279);
      setIsMobile(window.innerWidth <= 600);
    };
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [isMobile]);

  useEffect(() => {
    if (localStorage.getItem('logoutEvent')) {
      localStorage.removeItem('logoutEvent');
    }
    if (userCheckedOut) {
      resetTrip();
      setRefreshKey((prevKey) => prevKey + 1);
      setUserCheckedOut(false);
    }
    setLoaderProps({ showLoader: false, isInitializedFrom: '' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function capitalizeFirstLetter(inputString: string): string {
    return inputString.charAt(0).toUpperCase() + inputString.slice(1);
  }

  // ported the "handleOnSubmit" function from the tripDetailsForm of the WebStoreApp
  // with necessary modifications to ensure its functionality in this context.
  const handleOnSubmit = async (
    search: TDSScheduleCityToCityRequestBody,
    isOneWay: boolean,
    wheelchairCount: number
  ) => {
    setLoaderProps({
      showLoader: true,
      isInitializedFrom: '/'
    });
    const success = await withLoader(performTripSearch)(search, {
      isOneWay,
      wheelchairCount,
      modifyTrip: false,
      isDateChangedViaDateSelector: false
    });
    if (success) {
      router.push(localeSpecificPath('/purchase/schedules', lng));
    } else {
      setLoaderProps({
        showLoader: false,
        isInitializedFrom: ''
      });
      setTripSearchError(true);
    }
  };

  const drivenRewards = () => {
    if (customer !== null && loyalty !== null) {
      return (
        <>
          <div className={styles.text}>
            <h2 className={styles.title}>
              {t(`Welcome {{ firstName }} {{ lastName }},`, {
                firstName: customer.firstName ? capitalizeFirstLetter(customer.firstName) : '',
                lastName: customer.lastName ? capitalizeFirstLetter(customer.lastName) : ''
              })}
            </h2>
            <span className={styles.points}>
              {loyalty.totalLoyaltyPoints > 0
                ? t(`Your Driven Rewards point balance is: {{ totalLoyaltyPoints }}`, {
                    totalLoyaltyPoints: loyalty.totalLoyaltyPoints
                  })
                : t('You have no Driven Rewards points yet.')}
            </span>
          </div>
          <div className={styles.action}>
            <Button
              classNames={styles.dark}
              onClick={() => {
                window.location.href = `${localeSpecificPath('/account/mytrips/', lng)}`;
              }}
              secondary={true}
            >
              {t('View my Account')}
            </Button>
          </div>
        </>
      );
    }
    return (
      <>
        <div className={styles.text}>
          <h2 className={styles.title}>{t('Experience the perks of Trailways Driven Rewards')}</h2>
          <span className={styles.subtitle}>{subtitle}</span>
        </div>
        <div className={styles.action}>
          <Button
            classNames={styles.dark}
            secondary={true}
            onClick={() => {
              window.location.href = `${process.env.NEXT_PUBLIC_TRAILWAYS_SITE_BASE}/account/createaccount`;
            }}
            id="gtm-create_account"
          >
            {t('Create Account')}
          </Button>
          <Button
            classNames={styles.light}
            phantom={true}
            onClick={() => {
              window.location.href = localeSpecificPath('/account/login/', lng);
            }}
            id="gtm-sign_in"
          >
            {t('Sign In')}
          </Button>
        </div>
      </>
    );
  };

  return (
    <>
      {loaderProps.showLoader && <Loader isInitializedFrom={loaderProps.isInitializedFrom} />}
      <div className={styles.hero}>
        <div className={styles['hero-background-wrapper']}>
          <Image
            alt="Road traveling into scenic woods"
            aria-hidden="true"
            className={styles['hero-image']}
            layout="fill"
            priority={true}
            src={'/assets/images/global/heroes/home_page.webp'}
            style={{ objectFit: 'cover', objectPosition: 'center center' }}
          />
          <div className={styles['hero-background-gradient']}></div>
        </div>
        {boardingPassError && (
          <ToastError
            title={t('Order not found')}
            description={t('Please try using a different order number.')}
            onClose={() => setBoardingPassError(false)}
          />
        )}
        {tripSearchError && (
          <ToastError
            title={t('Your search did not yield any results.')}
            description={t('Please retry with a different origin/destination.')}
            onClose={() => setTripSearchError(false)}
          />
        )}
        {redirectMessage && (
          <ToastError
            title={t('Error')}
            description={t('Sorry, there was a problem. Please select your trip again.')}
            onClose={() => setRedirectMessage(false)}
          />
        )}
        <div className={styles.content}>
          <div className={styles.one}>
            <div className={styles.headline}>
              <h1 className={styles.title}>{t('Bus Tickets and Bus Charters from Trailways')}</h1>
              <span className={styles.subtitle}>{t('Where would you like to go today?')}</span>
            </div>
          </div>
          <div className={styles.two}>
            <div className={styles.rewards}>{drivenRewards()}</div>
          </div>
          <div className={styles.widget}>
            <Search
              departDate={tripDetailsForm?.departDate ? parseISO(tripDetailsForm.departDate) : null}
              destination={tripDetailsForm?.destination?.city_slug || ''}
              destinationState={''}
              handleOnSubmit={handleOnSubmit}
              isBookingWidget={true}
              key={refreshKey}
              origin={tripDetailsForm?.origin?.city_slug || ''}
              originState={''}
              passengerOptions={passengerOptions}
              returnDate={tripDetailsForm?.returnDate ? parseISO(tripDetailsForm.returnDate) : null}
              setPassengerOptions={(b: boolean) => setPassengerOptions(b)}
              type={tripDetailsForm?.isRoundTrip ? SearchType.ROUND_TRIP : SearchType.ONE_WAY || SearchType.ONE_WAY}
            />
            {!passengerOptions && (
              <div className={styles.boardingpass}>
                <ButtonTertiary light onClick={() => setIsOpen(true)} id="gtm-find_boarding_pass">
                  {t('Find boarding pass')}
                </ButtonTertiary>
                <Modal title={t('View Boarding Pass')} isOpen={isOpen} onClose={() => setIsOpen(false)}>
                  <FindBoardingPass onClose={() => setIsOpen(false)} onError={() => setBoardingPassError(true)} />
                </Modal>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default HomePageHero;
